/**
 * Schémas Zod — module agenda (épopée E26).
 */
import { z } from 'zod';
import { CALENDAR_ATTENDEE_STATUSES, CALENDAR_CONTACT_SCOPES, CALENDAR_EVENT_KINDS, CALENDAR_EVENT_STATUSES, CALENDAR_PROVIDERS, CALENDAR_REMINDER_CHANNELS, CALENDAR_SYNC_DIRECTIONS, CALENDAR_SYNC_STATUSES, } from '../enums/calendar.enum.js';
import { emailSchema } from './auth.schema.js';
import { paginationQuerySchema, uuidSchema } from './common.schema.js';
const optionalText = (max) => z.string().trim().max(max).nullable().optional();
const isoDateTimeSchema = z.string().datetime().nullable().optional();
/** Règle de récurrence normalisée : `FREQ=DAILY|WEEKLY|MONTHLY|YEARLY` (+ `INTERVAL`, `UNTIL`). */
const recurrenceRuleSchema = z
    .string()
    .trim()
    .max(255)
    .refine((value) => /^FREQ=(DAILY|WEEKLY|MONTHLY|YEARLY)(;[A-Z]+=[^;]+)*$/.test(value), {
    message: 'Règle de récurrence invalide (attendu : FREQ=DAILY ; INTERVAL et UNTIL en option).',
})
    .nullable()
    .optional();
/** Participant d'un événement (interne relié à un utilisateur, ou externe). */
const attendeeSchema = z.object({
    userId: uuidSchema.nullable().optional(),
    name: optionalText(180),
    email: z.string().trim().email().max(180).nullable().optional(),
    status: z.enum(CALENDAR_ATTENDEE_STATUSES).optional(),
});
/** Rappel exprimé en minutes avant le début de l'événement (jusqu'à 28 jours). */
const reminderSchema = z.object({
    minutesBefore: z.number().int().min(0).max(40320),
    channel: z.enum(CALENDAR_REMINDER_CHANNELS).optional(),
});
const calendarEventBaseSchema = {
    title: z.string().trim().min(1).max(180),
    description: optionalText(5000),
    location: optionalText(255),
    kind: z.enum(CALENDAR_EVENT_KINDS),
    status: z.enum(CALENDAR_EVENT_STATUSES).optional(),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    allDay: z.boolean().optional(),
    recurrenceRule: recurrenceRuleSchema,
    recurrenceUntil: isoDateTimeSchema,
    ownerUserId: uuidSchema.nullable().optional(),
    prospectId: uuidSchema.nullable().optional(),
    clientId: uuidSchema.nullable().optional(),
    opportunityId: uuidSchema.nullable().optional(),
    contactId: uuidSchema.nullable().optional(),
    contactScope: z.enum(CALENDAR_CONTACT_SCOPES).nullable().optional(),
    /** Demande l'envoi d'une confirmation de rendez-vous par e-mail aux participants. */
    confirmByEmail: z.boolean().optional(),
    /** Demande l'envoi d'une confirmation de rendez-vous par SMS (tracé, non encore diffusé). */
    confirmBySms: z.boolean().optional(),
    /**
     * Modèle nommé de confirmation retenu pour cet événement
     * (`settings.email.appointmentTemplates`) ; son nom devient l'objet du message.
     */
    confirmationTemplateId: z.string().trim().min(1).max(64).nullable().optional(),
    /** Agendas externes ciblés par la publication du RDV (push NORYVOX -> Google). */
    syncConnectionIds: z.array(uuidSchema).max(20).optional(),
    attendees: z.array(attendeeSchema).max(50).optional(),
    reminders: z.array(reminderSchema).max(10).optional(),
};
/** Vérifie la cohérence chronologique de l'événement. */
function checkRange(value) {
    if (value.startsAt === undefined || value.endsAt === undefined) {
        return true;
    }
    return new Date(value.endsAt).getTime() >= new Date(value.startsAt).getTime();
}
/** Paramètres de `GET /calendar/events`. */
export const calendarEventListQuerySchema = paginationQuerySchema.extend({
    filter: z
        .object({
        from: z.string().datetime().optional(),
        to: z.string().datetime().optional(),
        status: z.enum(CALENDAR_EVENT_STATUSES).optional(),
        kind: z.enum(CALENDAR_EVENT_KINDS).optional(),
        ownerUserId: uuidSchema.optional(),
        prospectId: uuidSchema.optional(),
        clientId: uuidSchema.optional(),
        opportunityId: uuidSchema.optional(),
        search: z.string().trim().max(180).optional(),
    })
        .optional(),
    /** `true` pour déplier les occurrences des événements récurrents sur la période. */
    expand: z.enum(['true', 'false']).optional(),
});
/** Corps de `POST /calendar/events`. */
export const calendarEventCreateSchema = z.object(calendarEventBaseSchema).refine(checkRange, {
    message: 'La fin de l’événement doit être postérieure ou égale à son début.',
    path: ['endsAt'],
});
/** Corps de `PATCH /calendar/events/:id`. */
export const calendarEventUpdateSchema = z
    .object(calendarEventBaseSchema)
    .partial()
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
})
    .refine(checkRange, {
    message: 'La fin de l’événement doit être postérieure ou égale à son début.',
    path: ['endsAt'],
});
/** Corps de `PATCH /calendar/events/:id/status`. */
export const calendarEventStatusUpdateSchema = z.object({
    status: z.enum(CALENDAR_EVENT_STATUSES),
});
/**
 * Corps de `POST /calendar/events/conflicts` : recherche les rendez-vous déjà
 * planifiés sur le créneau pour les utilisateurs internes concernés.
 */
export const calendarConflictQuerySchema = z
    .object({
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    /** Utilisateurs internes à contrôler (participants de l'événement en cours de saisie). */
    userIds: z.array(uuidSchema).max(50).optional(),
    /** Responsable de l'événement, contrôlé en plus des participants. */
    ownerUserId: uuidSchema.nullable().optional(),
    /** Événement à exclure du contrôle (lui-même lors d'une modification). */
    excludeEventId: uuidSchema.nullable().optional(),
})
    .refine(checkRange, {
    message: 'La fin de l’événement doit être postérieure ou égale à son début.',
    path: ['endsAt'],
});
/** Paramètres de `GET /calendar/sync-connections` (US-E26-06). */
export const calendarSyncConnectionListQuerySchema = paginationQuerySchema.extend({
    filter: z
        .object({
        provider: z.enum(CALENDAR_PROVIDERS).optional(),
        status: z.enum(CALENDAR_SYNC_STATUSES).optional(),
        /** Restreint aux connexions d'un utilisateur (réservé aux administrateurs de société). */
        userId: uuidSchema.optional(),
    })
        .optional(),
});
/** Paramètres de `GET /calendar/sync-connections/publish-targets`. */
export const calendarSyncPublishTargetsQuerySchema = z.object({
    userIds: z
        .preprocess((value) => {
        if (value === undefined) {
            return undefined;
        }
        return Array.isArray(value) ? value : [value];
    }, z.array(uuidSchema).max(50))
        .optional(),
});
/**
 * Corps de `POST /calendar/sync-connections` : connexion établie sans parcours OAuth
 * (flux ICS notamment). Les connexions Google sont créées par le retour du callback.
 */
export const calendarSyncConnectionCreateSchema = z.object({
    provider: z.enum(CALENDAR_PROVIDERS),
    externalAccountEmail: z.string().trim().email().max(190).nullable().optional(),
    calendarId: z.string().trim().max(190).nullable().optional(),
    syncDirection: z.enum(CALENDAR_SYNC_DIRECTIONS).optional(),
});
/** Corps de `PATCH /calendar/sync-connections/:id` : calendrier ciblé, sens et état. */
export const calendarSyncConnectionUpdateSchema = z
    .object({
    calendarId: z.string().trim().max(190).nullable().optional(),
    syncDirection: z.enum(CALENDAR_SYNC_DIRECTIONS).optional(),
    status: z.enum(CALENDAR_SYNC_STATUSES).optional(),
})
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
});
/** Paramètres de `GET /calendar/sync-connections/google/authorize`. */
export const calendarSyncAuthorizeQuerySchema = z.object({
    /** Calendrier distant ciblé ; `primary` par défaut. */
    calendarId: z.string().trim().max(190).optional(),
    syncDirection: z.enum(CALENDAR_SYNC_DIRECTIONS).optional(),
    /** Adresse du compte Google à raccorder, utilisée comme indice et vérifiée au retour OAuth. */
    googleAccountEmail: emailSchema.max(190).optional(),
});
/**
 * Paramètres de `GET /calendar/sync-connections/google/callback` : le code d'autorisation
 * et le jeton d'état signé, ou l'erreur renvoyée par Google lorsque l'utilisateur refuse.
 */
export const calendarSyncCallbackQuerySchema = z.object({
    code: z.string().trim().min(1).max(2048).optional(),
    state: z.string().trim().min(1).max(2048),
    error: z.string().trim().max(120).optional(),
});
//# sourceMappingURL=calendar.schema.js.map